import { useState, useEffect, useRef } from "react";

interface WebSocketOptions {
  heartbeat?: boolean; // 是否启用心跳
  heartbeatInterval?: number; // 心跳间隔（毫秒）
  heartbeatMessage?: string; // 心跳消息
  reconnectLimit?: number; // 最大重连次数
  reconnectInterval?: number; // 重连间隔（毫秒）
}

type ReadyState = 0 | 1 | 2 | 3;

const useWebSocket = (url: string | null, options: WebSocketOptions = {}) => {
  const {
    heartbeat = false,
    heartbeatInterval = 50000,
    heartbeatMessage = "ping",
    reconnectLimit = 5,
    reconnectInterval = 3000,
  } = options;

  const [lastMessage, setLastMessage] = useState<MessageEvent | null>(null);
  const [readyState, setReadyState] = useState<ReadyState>(
    WebSocket.CONNECTING,
  );

  const ws = useRef<WebSocket | null>(null);
  const heartbeatTimer = useRef<number | null>(null);
  const reconnectTimer = useRef<number | null>(null);
  const reconnectCount = useRef<number>(0);

  const connect = () => {
    if (!url || reconnectCount.current >= reconnectLimit) {
      setReadyState(WebSocket.CLOSED);
      return;
    }

    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      return;
    }

    setReadyState(WebSocket.CONNECTING);

    const socket = new WebSocket(url);
    ws.current = socket;

    socket.onopen = () => {
      console.log("WebSocket Connected");
      reconnectCount.current = 0;
      setReadyState(WebSocket.OPEN);
      // 启动心跳
      if (heartbeat) {
        startHeartbeat();
      }
    };

    socket.onmessage = (event: MessageEvent) => {
      // 重置心跳
      if (heartbeat) {
        startHeartbeat();
      }
      // 如果服务器回复了心跳消息，则不将其作为普通消息处理
      if (event.data === "pong") {
        return;
      }
      setLastMessage(event);
    };

    socket.onclose = () => {
      console.log("WebSocket Disconnected");
      setReadyState(WebSocket.CLOSED);
      // 关闭时停止心跳
      stopHeartbeat();
      if (reconnectCount.current < reconnectLimit) {
        reconnectTimer.current = setTimeout(connect, reconnectInterval);
      }
    };

    socket.onerror = (error) => {
      console.error("WebSocket Error:", error);
    };
  };

  const startHeartbeat = () => {
    // 清除旧的定时器
    if (heartbeatTimer.current) {
      clearTimeout(heartbeatTimer.current);
    }
    // 启动新的定时器
    heartbeatTimer.current = setTimeout(() => {
      if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        ws.current.send(heartbeatMessage);
        // 发送心跳后，再设置一个超时，如果这段时间内没收到任何消息，则认为连接断开
        heartbeatTimer.current = setTimeout(() => {
          ws.current?.close();
        }, heartbeatInterval / 2);
      }
    }, heartbeatInterval);
  };

  const stopHeartbeat = () => {
    if (heartbeatTimer.current) {
      clearTimeout(heartbeatTimer.current);
      heartbeatTimer.current = null;
    }
  };

  useEffect(() => {
    connect();

    return () => {
      // 清理所有定时器和连接
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
      }
      stopHeartbeat();
      if (ws.current) {
        ws.current.onclose = null;
        ws.current.close();
      }
    };
  }, [url]);

  const sendMessage = (message: any) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(message);
    } else {
      console.error("WebSocket is not connected.");
    }
  };

  return { lastMessage, readyState, sendMessage };
};

export default useWebSocket;
